import moment from 'moment-timezone';

import { getIntl } from './IntlInstance';

import {
  getCurrentUser,
  getOriginalProfile,
} from './Auth';
import {
  getSetting,
  getUserSetting,
} from './Settings';

export const detectUserTimeZone = (disableCache = false) => (
  moment.tz.guess(disableCache)
);

export const getUserTimeZone = (profile = null) => {
  let user = profile || getCurrentUser();

  if (getOriginalProfile()) {
    user = getOriginalProfile();
  }

  if (!(user && user.timezone && user.timezone.timezone)) {
    return null;
  }

  let { timezone } = user.timezone;
  if (!timezone) {
    timezone = detectUserTimeZone(true);
  }
  return timezone;
};

export const hasRelativeTime = () => {
  if (typeof getUserSetting('relative_date_time') === 'boolean') {
    return getUserSetting('relative_date_time');
  }
  return getSetting('relative_date_time');
};

export const getRawDate = (date, tz = null) => {
  const timezone = tz || getUserTimeZone();
  return moment.tz(date, 'GMT').tz(timezone);
};

export const getRawUTCDate = (date, tz = null) => {
  const timezone = tz || getUserTimeZone();
  return moment.tz(date, timezone).utc();
};

export const getDateDiff = (date) => {
  const intl = getIntl();
  const ddate = getRawDate(date);
  const today = moment().tz('GMT');
  const diff = today.diff(ddate);

  const humanizedDiff = moment.duration(diff).humanize();
  return intl.formatMessage({ defaultMessage: '{humanizedDiff} ago' }, { humanizedDiff });
};

export const getDate = (date) => {
  const ddate = getRawDate(date);
  return ddate.format('MMMM D, YYYY');
};

export const getDateTime = (date) => {
  const ddate = getRawDate(date);
  return ddate.format('MMMM D, YYYY h:mm a');
};

export const getDateFormatted = (date, format = 'MMMM D, YYYY') => {
  const ddate = moment(getRawDate(date));
  return ddate.format(format);
};

export const getFormattedDate = (date, tz = null) => {
  const dateFormat = getUserSetting('date_format') || getSetting('date_format');
  const ddate = moment(getRawDate(date, tz));
  return ddate.format(`${dateFormat}`);
};

export const getFormattedTime = (date, tz = null, defaultTimeFormat = null) => {
  let timeFormat = getUserSetting('time_format');
  if ((timeFormat === 'hidden' || !timeFormat) && defaultTimeFormat) {
    timeFormat = defaultTimeFormat;
  }
  const ddate = moment(getRawDate(date, tz));
  return ddate.format(`${timeFormat}`);
};

export const datePrefix = (prefix = 'on') => (
  hasRelativeTime() ? '' : ` ${prefix} `
);

export const getFormattedDateTime = (date, inTooltip = false, tz = null) => {
  if (!inTooltip && hasRelativeTime()) {
    return getDateDiff(getRawDate(date, tz));
  }

  const dateFormat = getUserSetting('date_format') || getSetting('date_format');
  const timeFormat = getUserSetting('time_format') || getSetting('time_format');
  const ddate = moment(getRawDate(date, tz));
  if (['hidden'].includes(timeFormat)) {
    return ddate.format(`${dateFormat}`);
  }
  return ddate.format(`${dateFormat} ${timeFormat}`);
};

export const getDateRanges = (intl) => ([
  {
    id: '1-day',
    label: intl.formatMessage({ defaultMessage: 'Last 24 hours' }),
    days: 1,
  },
  {
    id: '7-days',
    label: intl.formatMessage({ defaultMessage: 'Last 7 days' }),
    days: 7,
  },
  {
    id: '30-days',
    label: intl.formatMessage({ defaultMessage: 'Last 30 days' }),
    days: 30,
  },
  {
    id: '365-days',
    label: intl.formatMessage({ defaultMessage: 'Last 365 days' }),
    days: 365,
  },
  {
    id: 'custom',
    label: intl.formatMessage({ defaultMessage: 'Custom' }),
  },
]);

export const getExpirationDateRanges = (intl) => ([
  {
    id: 'expired',
    label: intl.formatMessage({ defaultMessage: 'Expired' }),
  },
  {
    id: 'next-1-day',
    label: intl.formatMessage({ defaultMessage: 'Next 24 hours' }),
    days: -1,
  },
  {
    id: 'next-7-days',
    label: intl.formatMessage({ defaultMessage: 'Next 7 days' }),
    days: -7,
  },
  {
    id: 'next-30-days',
    label: intl.formatMessage({ defaultMessage: 'Next 30 days' }),
    days: -30,
  },
  {
    id: 'custom',
    label: intl.formatMessage({ defaultMessage: 'Custom' }),
  },
]);

export const getDateRange = (rangeId, intl) => (
  getDateRanges(intl).concat(getExpirationDateRanges(intl)).find(r => (r.id === rangeId))
);

export const formatSettings = (originalSettings, isSelfEdit = false, intl) => {
  const settings = { ...originalSettings };

  let hiddenSettings = [];
  Object.keys(settings).forEach((key) => {
    if (['date_format', 'time_format'].includes(key)) {
      settings[key].description_hidden = settings.relative_date_time.value === false;
      settings[key].labels = [];
      settings[key].values.forEach((value) => {
        if (['hidden'].includes(value)) {
          settings[key].labels.push(value);
        } else {
          settings[key].labels.push(getDateFormatted('2023-08-14 21:15:00', value));
        }
      });
    }

    if (key === 'timezone') {
      settings[key].description_hidden = settings[key].value !== 'auto';
      if (isSelfEdit && !getOriginalProfile()) {
        settings[key].labels[0] = intl.formatMessage({
          defaultMessage: 'Auto-detect ({timezone})',
        }, { timezone: getUserTimeZone() });
      }
    }

    if (settings[key].hide_when_checked && settings[key].value === true) {
      hiddenSettings = hiddenSettings.concat(settings[key].hide_when_checked);
    }
  });

  Object.keys(settings).forEach((key) => {
    settings[key].hidden = hiddenSettings.includes(key);
  });

  return settings;
};
